// src/components/Figma/Header.tsx
import { useState, useEffect } from "react";

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header className="sticky top-0 z-40 border-0 bg-[#DCE9FC]/95 backdrop-blur">
      <div className="px-4 sm:px-6 md:px-10 py-4 md:py-5 flex items-center justify-between container mx-auto">
        {/* Logo */}
        <div className="flex w-[90px] sm:w-[111.6px] h-7 sm:h-9 justify-center items-center">
          <img
            src="/dfuse-logo.svg"
            alt="dfuse"
            className="w-full h-full object-contain"
          />
        </div>

        {/* Desktop Navigation (no URL change) */}
        <nav className="hidden lg:flex gap-4 xl:gap-6">
          <button
            type="button"
            onClick={() => scrollToSection("how-it-works")}
            className="bg-transparent border-none text-[#2C2C2C] font-family-reddit-sans text-base xl:text-[18px] font-medium leading-6 hover:text-dark-green active:text-pastel-soft-green transition-colors cursor-pointer"
          >
            How it Works
          </button>
          <button
            type="button"
            onClick={() => scrollToSection("who-its-for")}
            className="bg-transparent border-none text-[#2C2C2C] font-family-reddit-sans text-base xl:text-[18px] font-medium leading-6 hover:text-dark-green active:text-pastel-soft-green transition-colors cursor-pointer"
          >
            Who is it for
          </button>
          <button
            type="button"
            onClick={() => scrollToSection("articles")}
            className="bg-transparent border-none text-[#2C2C2C] font-family-reddit-sans text-base xl:text-[18px] font-medium leading-6 hover:text-dark-green active:text-pastel-soft-green transition-colors cursor-pointer"
          >
            Blogs
          </button>
          <button
            type="button"
            onClick={() => scrollToSection("real-stories")}
            className="bg-transparent border-none text-[#2C2C2C] font-family-reddit-sans text-base xl:text-[18px] font-medium leading-6 hover:text-dark-green active:text-pastel-soft-green transition-colors cursor-pointer"
          >
            Real Stories
          </button>
        </nav>

        {/* Desktop Buttons (preview only, no routing) */}
        <div className="hidden md:flex items-center gap-2 lg:gap-3">
          <button
            type="button"
            className="flex px-4 lg:px-7 py-2 lg:py-3 justify-center items-center gap-2 rounded-[20px] border-[1.5px] border-[#34A853] bg-white text-[#2C2C2C] font-family-gabarito text-base lg:text-[18px] font-medium leading-6 hover:bg-border-dark-green active:bg-border-dark-green active:text-dark-green transition-colors whitespace-nowrap cursor-default"
          >
            Try For Free
          </button>
          <button
            type="button"
            className="flex px-4 lg:px-7 py-2 lg:py-3 justify-center items-center gap-2 rounded-[20px] bg-[#34A853] text-white font-family-gabarito text-base lg:text-[18px] font-medium leading-6 hover:bg-dark-green active:bg-pastel-soft-green active:text-dark-green transition-colors cursor-default"
          >
            Login
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          type="button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden flex flex-col gap-1.5 w-6 h-6 justify-center items-center"
          aria-label="Toggle menu"
        >
          <span
            className={`w-6 h-0.5 bg-[#2C2C2C] transition-all ${
              isMenuOpen ? "rotate-45 translate-y-2" : ""
            }`}
          />
          <span
            className={`w-6 h-0.5 bg-[#2C2C2C] transition-all ${
              isMenuOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`w-6 h-0.5 bg-[#2C2C2C] transition-all ${
              isMenuOpen ? "-rotate-45 -translate-y-2" : ""
            }`}
          />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-opacity duration-300 ease-in-out ${
          isMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Dark Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-[280px] bg-[#DCE9FC] shadow-2xl transform transition-transform duration-300 ease-out ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full px-6 py-8">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setIsMenuOpen(false)}
              className="self-end mb-8 p-2 rounded-full hover:bg-white/50 transition-colors"
              aria-label="Close menu"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#2C2C2C"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Navigation Links (smooth scroll, no URL change) */}
            <nav className="flex flex-col gap-2 mb-8">
              {[
                { id: "how-it-works", label: "How it Works" },
                { id: "who-its-for", label: "Who is it for" },
                { id: "articles", label: "Blogs" },
                { id: "real-stories", label: "Real Stories" },
              ].map((link, index) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => {
                    scrollToSection(link.id);
                    setIsMenuOpen(false);
                  }}
                  className={`text-left bg-transparent border-none text-[#2C2C2C] font-family-reddit-sans text-lg font-medium leading-6 hover:text-dark-green active:text-pastel-soft-green transition-all duration-300 ease-out py-3 px-4 rounded-lg hover:bg-white/50 transform ${
                    isMenuOpen
                      ? "translate-x-0 opacity-100"
                      : "translate-x-4 opacity-0"
                  }`}
                  style={{
                    transitionDelay: isMenuOpen
                      ? `${index * 50 + 100}ms`
                      : "0ms",
                  }}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Buttons (preview only) */}
            <div className="flex flex-col gap-3 mt-auto">
              <button
                type="button"
                className={`flex w-full px-4 py-3 justify-center items-center gap-2 rounded-[20px] border-[1.5px] border-[#34A853] bg-white text-[#2C2C2C] font-family-gabarito text-base font-medium leading-6 hover:bg-border-dark-green active:bg-border-dark-green active:text-dark-green transition-all duration-300 ease-out transform ${
                  isMenuOpen
                    ? "translate-y-0 opacity-100"
                    : "translate-y-4 opacity-0"
                }`}
                style={{ transitionDelay: isMenuOpen ? "300ms" : "0ms" }}
              >
                Try For Free
              </button>
              <button
                type="button"
                className={`flex w-full px-4 py-3 justify-center items-center gap-2 rounded-[20px] bg-[#34A853] text-white font-family-gabarito text-base font-medium leading-6 hover:bg-dark-green active:bg-pastel-soft-green active:text-dark-green transition-all duration-300 ease-out transform ${
                  isMenuOpen
                    ? "translate-y-0 opacity-100"
                    : "translate-y-4 opacity-0"
                }`}
                style={{ transitionDelay: isMenuOpen ? "350ms" : "0ms" }}
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
